import { message } from 'antd';
import { useState, useCallback, useEffect, useRef } from 'react';
import { fetchAllPages, IncompleteFetchError, isAbortError, PagedFetcher } from '@shared/utils';

const MESSAGE_KEY = 'massive-export';

interface MassiveExportOptions {
  resetKey?: unknown;
}

export const useMassiveExport = <T>(fetcher: PagedFetcher<T>, { resetKey }: MassiveExportOptions = {}) => {
  const [exportedData, setExportedData] = useState<T[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  const cancelExport = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      message.destroy(MESSAGE_KEY);
    }
  }, []);

  const resetExport = useCallback(() => {
    cancelExport();
    setExportedData((prev) => (prev.length ? [] : prev));
  }, [cancelExport]);

  useEffect(() => cancelExport, [cancelExport]);

  // Compared by value so a new-but-equal filters array doesn't wipe a finished export.
  const resetSignature = JSON.stringify(resetKey ?? null);
  useEffect(() => {
    resetExport();
  }, [resetSignature, resetExport]);

  const handleExport = useCallback(
    async (query: Record<string, any> = {}) => {
      cancelExport();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setExportedData([]);
      message.loading({ key: MESSAGE_KEY, content: 'Sənəd hazırlanır...', duration: 0 });

      try {
        const allData = await fetchAllPages(fetcher, query, {
          signal: controller.signal,
          onProgress: (loaded, total) => {
            if (total > 1) message.loading({ key: MESSAGE_KEY, content: `Sənəd hazırlanır... (${loaded}/${total})`, duration: 0 });
          },
        });

        message.success({ key: MESSAGE_KEY, content: 'Sənəd hazırdır. Yüklə butonuna klikləyin' });
        setExportedData(allData);
        return allData;
      } catch (error) {
        if (isAbortError(error)) return [];
        const content =
          error instanceof IncompleteFetchError ? `${error.totalPages} səhifədən ${error.failedPages.length}-i yüklənə bilmədi. Yenidən cəhd edin.` : 'Sənəd hazırlana bilmədi. Yenidən cəhd edin.';
        message.error({ key: MESSAGE_KEY, content });
        return [];
      } finally {
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      }
    },
    [fetcher, cancelExport],
  );

  return { handleExport, exportedData, cancelExport, resetExport };
};
